import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Container,
  Stack,
  Autocomplete
} from '@mui/material';
import AdminLayout from '../../components/AdminLayout';
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Importaciones de Firebase
import { getFirestore, collection, addDoc, getDocs, query, where, doc, updateDoc, deleteDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import app from '../../config/firebase';
import { patientsService } from '../../services/firebaseServices';

// Importar servicio de email
import emailService from '../../services/emailService';
// Importar servicio de recordatorios
import reminderService from '../../services/reminderService';

// Inicializar Firestore
const db = getFirestore(app);

const AgendaPrivada = () => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [horariosDisponibles, setHorariosDisponibles] = useState([]);
  const [pacientesExistentes, setPacientesExistentes] = useState([]);
  const [loadingPacientesExistentes, setLoadingPacientesExistentes] = useState(false);
  const [tipoPacienteCita, setTipoPacienteCita] = useState('nuevo');
  const [pacienteSeleccionadoId, setPacienteSeleccionadoId] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [appointmentData, setAppointmentData] = useState({
    fecha: dayjs().format('YYYY-MM-DD'),
    hora: '09:00',
    pacienteId: '',
    paciente: '',
    dueño: '',
    telefono: '',
    email: '',
    tipo: 'consulta',
    motivo: '',
    estado: 'programada',
    observaciones: '',
    esRecurrente: false,
    tipoRecurrencia: 'semanal',
    cantidadRecurrencias: 4,
    createdAt: null,
    updatedAt: null
  });

  const tiposCita = [
    { value: 'consulta', label: 'Consulta General', color: 'primary' },
    { value: 'vacunacion', label: 'Vacunación', color: 'success' },
    { value: 'cirugia', label: 'Cirugía', color: 'warning' },
    { value: 'control', label: 'Control', color: 'info' },
    { value: 'emergencia', label: 'Emergencia', color: 'error' },
    { value: 'limpieza_dental', label: 'Limpieza Dental', color: 'secondary' }
  ];

  const estadosCita = [
    { value: 'programada', label: 'Programada', color: 'default' },
    { value: 'confirmada', label: 'Confirmada', color: 'success' },
    { value: 'en_curso', label: 'En Curso', color: 'warning' },
    { value: 'completada', label: 'Completada', color: 'success' },
    { value: 'cancelada', label: 'Cancelada', color: 'error' },
    { value: 'no_asistio', label: 'No Asistió', color: 'error' },
    { value: 'urgente', label: 'Urgente', color: 'error' }
  ];
  
  const tiposRecurrencia = [
    { value: 'semanal', label: 'Semanal', dias: 7 },
    { value: 'quincenal', label: 'Quincenal', dias: 14 },
    { value: 'mensual', label: 'Mensual', dias: 30 }
  ];

  const todosLosHorarios = useMemo(() => [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
  ], []);
  
  // Función para obtener los horarios disponibles según la fecha seleccionada
  const getHorariosDisponibles = useCallback(() => {
    const ahora = dayjs();
    const fechaSeleccionada = selectedDate.format('YYYY-MM-DD');
    const hoy = ahora.format('YYYY-MM-DD');
    
    // Si la fecha seleccionada es futura, mostrar todos los horarios
    if (fechaSeleccionada > hoy) {
      return todosLosHorarios;
    }
    
    // Si la fecha seleccionada es pasada, no mostrar horarios
    if (fechaSeleccionada < hoy) {
      return [];
    }
    
    // Si es hoy, mostrar solo horarios futuros
    const horaActual = ahora.format('HH:mm');
    return todosLosHorarios.filter(hora => hora > horaActual);
  }, [selectedDate, todosLosHorarios]);
  
  // Actualizar los horarios disponibles cuando cambia la fecha
  useEffect(() => {
    setHorariosDisponibles(getHorariosDisponibles());
    // Actualizar cada minuto si estamos viendo el día actual
    const esHoy = selectedDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
    
    if (esHoy) {
      const intervalo = setInterval(() => {
        setHorariosDisponibles(getHorariosDisponibles());
      }, 60000); // Actualizar cada minuto
      
      return () => clearInterval(intervalo);
    }
  }, [selectedDate, getHorariosDisponibles]);

  // Cargar pacientes para permitir seleccionar existentes en la cita
  useEffect(() => {
    const cargarPacientesExistentes = async () => {
      setLoadingPacientesExistentes(true);
      try {
        const response = await patientsService.getAllPatients();
        if (response.success) {
          setPacientesExistentes(response.data || []);
        }
      } catch (error) {
        console.error('Error al cargar pacientes existentes:', error);
      } finally {
        setLoadingPacientesExistentes(false);
      }
    };

    cargarPacientesExistentes();
  }, []);

  // Inicializar servicio de recordatorios
  useEffect(() => {
    console.log('🚀 Iniciando servicio de recordatorios automáticos');
    reminderService.start();
    
    // Cleanup cuando se desmonte el componente
    return () => {
      console.log('🛑 Deteniendo servicio de recordatorios');
      reminderService.stop();
    };
  }, []);

  // Función para cargar las citas desde Firestore
  const cargarCitas = useCallback(async () => {
    setLoading(true);
    try {
      const fechaSeleccionada = selectedDate.format('YYYY-MM-DD');
      const citasRef = collection(db, 'citas');
      
      // Ahora podemos usar orderBy ya que tenemos un índice creado
      const q = query(
        citasRef, 
        where('fecha', '==', fechaSeleccionada),
        orderBy('hora', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      const citasData = [];
      
      querySnapshot.forEach(doc => {
        citasData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setCitas(citasData);
    } catch (error) {
      console.error("Error al cargar las citas:", error);
      
      // Si hay un error de índice, intentamos con la carga alternativa
      if (error.message.includes('index')) {
        console.log("Error de índice detectado, usando método alternativo");
        try {
          const fechaSeleccionada = selectedDate.format('YYYY-MM-DD');
          const citasRef = collection(db, 'citas');
          
          // Método alternativo sin orderBy
          const qAlternative = query(
            citasRef, 
            where('fecha', '==', fechaSeleccionada)
          );
          
          const querySnapshot = await getDocs(qAlternative);
          const citasData = [];
          
          querySnapshot.forEach(doc => {
            citasData.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          // Ordenamos manualmente las citas por hora
          citasData.sort((a, b) => {
            if (a.hora < b.hora) return -1;
            if (a.hora > b.hora) return 1;
            return 0;
          });
          
          setCitas(citasData);
          return; // Salimos si el método alternativo fue exitoso
        } catch (altError) {
          console.error("Error en método alternativo:", altError);
        }
      }
      
      setSnackbar({
        open: true,
        message: "Error al cargar las citas: " + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Efecto para cargar las citas desde Firestore
  useEffect(() => {
    cargarCitas();
  }, [cargarCitas]);

  // Efecto para cargar las citas desde Firestore
  useEffect(() => {
    cargarCitas();
  }, [cargarCitas]);

  // Filtrado y búsqueda de citas
  const citasDelDia = citas
    .filter(cita => cita.fecha === selectedDate.format('YYYY-MM-DD'))
    .filter(cita => {
      // Aplicar búsqueda por nombre del paciente
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        // Priorizar búsqueda por nombre de paciente
        const matchPaciente = cita.paciente && cita.paciente.toLowerCase().includes(term);
        
        // Si no hay coincidencia en paciente, buscar en otros campos
        if (!matchPaciente) {
          return (
            (cita.dueño && cita.dueño.toLowerCase().includes(term)) ||
            (cita.motivo && cita.motivo.toLowerCase().includes(term)) ||
            (cita.telefono && cita.telefono.includes(term)) ||
            (cita.email && cita.email.toLowerCase().includes(term))
          );
        }
        return matchPaciente;
      }
      return true;
    })
    .filter(cita => filtroTipo ? cita.tipo === filtroTipo : true)
    .filter(cita => filtroEstado ? cita.estado === filtroEstado : true);

  const handleNewAppointment = () => {
    setEditingAppointment(null);
    setTipoPacienteCita('nuevo');
    setPacienteSeleccionadoId('');
    setAppointmentData({
      fecha: selectedDate.format('YYYY-MM-DD'),
      hora: '09:00',
      pacienteId: '',
      paciente: '',
      dueño: '',
      telefono: '',
      email: '',
      tipo: 'consulta',
      motivo: '',
      estado: 'programada',
      observaciones: '',
      esRecurrente: false,
      tipoRecurrencia: 'semanal',
      cantidadRecurrencias: 4,
      createdAt: null,
      updatedAt: null
    });
    setDialogOpen(true);
  };

  const handleEditAppointment = (cita) => {
    setEditingAppointment(cita.id);
    const esPacienteExistente = Boolean(cita.pacienteId);
    setTipoPacienteCita(esPacienteExistente ? 'existente' : 'nuevo');
    setPacienteSeleccionadoId(cita.pacienteId || '');
    setAppointmentData({
      ...cita,
      pacienteId: cita.pacienteId || '',
      hora: cita.hora24 || cita.hora || '09:00'
    });
    setDialogOpen(true);
  };

  const handleTipoPacienteCitaChange = (event) => {
    const nuevoTipo = event.target.value;
    setTipoPacienteCita(nuevoTipo);

    if (nuevoTipo === 'nuevo') {
      setPacienteSeleccionadoId('');
      setAppointmentData(prev => ({
        ...prev,
        pacienteId: '',
        paciente: '',
        dueño: '',
        telefono: '',
        email: ''
      }));
      return;
    }

    setAppointmentData(prev => ({
      ...prev,
      pacienteId: '',
      paciente: '',
      dueño: '',
      telefono: '',
      email: ''
    }));
  };

  const handleAutocompletePacienteExistente = (_, paciente) => {
    if (!paciente) {
      setPacienteSeleccionadoId('');
      setAppointmentData(prev => ({
        ...prev,
        pacienteId: '',
        paciente: '',
        dueño: '',
        telefono: '',
        email: ''
      }));
      return;
    }

    const telefonoPaciente =
      paciente.propietarioTelefono ||
      paciente.telefono ||
      paciente.phone ||
      paciente.celular ||
      paciente.contactoEmergenciaTelefono ||
      '';

    const emailPaciente =
      paciente.propietarioEmail ||
      paciente.email ||
      paciente.correo ||
      '';

    setPacienteSeleccionadoId(paciente.id);
    setAppointmentData(prev => ({
      ...prev,
      pacienteId: paciente.id,
      paciente: paciente.nombre || '',
      dueño: paciente.propietario || paciente['dueño'] || paciente.dueno || '',
      telefono: telefonoPaciente,
      email: emailPaciente
    }));
  };
  
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setAppointmentData({
      ...appointmentData,
      [name]: value
    });
  };

  // Función para validar email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizeHourForSave = (hora) => {
    if (!hora) return null;
    const match = String(hora).trim().match(/(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const h = parseInt(match[1], 10);
    const m = match[2];
    if (Number.isNaN(h) || h < 0 || h > 23) return null;
    return `${String(h).padStart(2, '0')}:${m}`;
  };

  // Función para enviar recordatorio por email (usando EmailJS)
  const sendEmailReminder = async (citaData) => {
    try {
      console.log('📧 Enviando recordatorio de cita:', citaData);
      const result = await emailService.sendAppointmentReminder(citaData);
      console.log('📧 Resultado del envío:', result);
      
      // Validación segura del resultado
      if (result && typeof result === 'object' && result.success === true) {
        console.log('Email enviado exitosamente:', result);
        return true;
      } else {
        console.error('Error enviando email:', result.message);
        // Mostrar mensaje de error específico al usuario
        setSnackbar({
          open: true,
          message: `Error enviando email: ${result.message}`,
          severity: 'error'
        });
        return false;
      }
    } catch (error) {
      console.error('Error enviando recordatorio:', error);
      setSnackbar({
        open: true,
        message: `Error enviando recordatorio: ${error.message}`,
        severity: 'error'
      });
      return false;
    }
  };

  const handleSaveAppointment = async () => {
    try {
      if (!appointmentData.paciente || !String(appointmentData.paciente).trim()) {
        setSnackbar({
          open: true,
          message: "Debes ingresar o seleccionar un paciente.",
          severity: 'error'
        });
        return;
      }

      if (tipoPacienteCita === 'existente' && !appointmentData.pacienteId) {
        setSnackbar({
          open: true,
          message: "Selecciona un paciente existente para la cita.",
          severity: 'error'
        });
        return;
      }

      if (tipoPacienteCita === 'nuevo' && (!appointmentData.dueño || !String(appointmentData.dueño).trim())) {
        setSnackbar({
          open: true,
          message: "Para paciente nuevo, el nombre del dueño es obligatorio.",
          severity: 'error'
        });
        return;
      }

      const horaNormalizada = normalizeHourForSave(appointmentData.hora);
      if (!horaNormalizada) {
        setSnackbar({
          open: true,
          message: "La hora de la cita no es válida. Selecciona una hora del listado.",
          severity: 'error'
        });
        return;
      }

      // Validar que la fecha y hora no sean en el pasado (solo para citas nuevas)
      if (!editingAppointment) {
        const fechaCita = dayjs(`${appointmentData.fecha} ${horaNormalizada}`, 'YYYY-MM-DD HH:mm');
        const ahora = dayjs();
        
        if (fechaCita.isBefore(ahora)) {
          setSnackbar({
            open: true,
            message: "No se puede crear una cita en una fecha y hora que ya pasó. Selecciona una fecha y hora futura.",
            severity: 'error'
          });
          return;
        }
      }

      // Validar email si se proporcionó
      if (appointmentData.email && !validateEmail(appointmentData.email)) {
        setSnackbar({
          open: true,
          message: "Por favor ingrese un email válido",
          severity: 'error'
        });
        return;
      }

      const now = serverTimestamp();
      let citaData = {
        ...appointmentData,
        hora: horaNormalizada,
        hora24: horaNormalizada
      };
      
      if (editingAppointment) {
        // Actualizar cita existente
        const citaRef = doc(db, 'citas', editingAppointment);
        citaData.updatedAt = now;
        
        await updateDoc(citaRef, citaData);
        
        // Enviar recordatorio si hay email
        if (citaData.email) {
          const emailSent = await sendEmailReminder(citaData);
          if (emailSent) {
            setSnackbar({
              open: true,
              message: "Cita actualizada y recordatorio enviado por email",
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: "Cita actualizada. Error al enviar recordatorio por email",
              severity: 'warning'
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: "Cita actualizada correctamente",
            severity: 'success'
          });
        }
      } else {
        // Crear nueva cita o citas recurrentes
        // Eliminamos propiedades null para evitar errores
        Object.keys(citaData).forEach(key => {
          if (citaData[key] === null) {
            delete citaData[key];
          }
        });
        
        // Agregamos timestamps y campos para recordatorios
        citaData.createdAt = now;
        citaData.updatedAt = now;
        citaData.recordatorioEnviado = false; // Para el sistema de recordatorios automáticos
        
        if (citaData.esRecurrente) {
          // Crear citas recurrentes
          const tipoRec = tiposRecurrencia.find(t => t.value === citaData.tipoRecurrencia);
          const fechaBase = dayjs(citaData.fecha);
          const cantidadCitas = parseInt(citaData.cantidadRecurrencias) || 1;
          const diasIntervalo = tipoRec ? tipoRec.dias : 7;
          
          let citasCreadas = 0;
          const citasPromesas = [];
          
          // Creamos la primera cita (la original)
          const citaOriginalData = { ...citaData };
          // No necesitamos guardar estos campos en cada cita individual
          delete citaOriginalData.esRecurrente;
          delete citaOriginalData.tipoRecurrencia;
          delete citaOriginalData.cantidadRecurrencias;
          
          citasPromesas.push(addDoc(collection(db, 'citas'), citaOriginalData));
          citasCreadas++;
          
          // Creamos las citas recurrentes adicionales
          for (let i = 1; i < cantidadCitas; i++) {
            const nuevaFecha = fechaBase.add(i * diasIntervalo, 'day');
            const nuevaCitaData = {
              ...citaOriginalData,
              fecha: nuevaFecha.format('YYYY-MM-DD')
            };
            
            citasPromesas.push(addDoc(collection(db, 'citas'), nuevaCitaData));
            citasCreadas++;
          }
          
          await Promise.all(citasPromesas);
          
          // Enviar recordatorio por email si está disponible
          if (citaOriginalData.email) {
            const emailSent = await sendEmailReminder(citaOriginalData);
            if (emailSent) {
              setSnackbar({
                open: true,
                message: `${citasCreadas} citas recurrentes agendadas y recordatorio enviado por email`,
                severity: 'success'
              });
            } else {
              setSnackbar({
                open: true,
                message: `${citasCreadas} citas recurrentes agendadas. Error al enviar recordatorio por email`,
                severity: 'warning'
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: `${citasCreadas} citas recurrentes agendadas correctamente`,
              severity: 'success'
            });
          }
        } else {
          // Crear una sola cita
          await addDoc(collection(db, 'citas'), citaData);
          
          // Enviar recordatorio por email si está disponible
          if (citaData.email) {
            const emailSent = await sendEmailReminder(citaData);
            if (emailSent) {
              setSnackbar({
                open: true,
                message: "Cita agendada y recordatorio enviado por email",
                severity: 'success'
              });
            } else {
              setSnackbar({
                open: true,
                message: "Cita agendada. Error al enviar recordatorio por email",
                severity: 'warning'
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: "Cita agendada correctamente",
              severity: 'success'
            });
          }
        }
      }
      
      setDialogOpen(false);
      cargarCitas(); // Recargar las citas
    } catch (error) {
      console.error("Error al guardar la cita:", error);
      setSnackbar({
        open: true,
        message: "Error al guardar la cita: " + error.message,
        severity: 'error'
      });
    }
  };

  const handleDeleteAppointment = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta cita?')) {
      try {
        const citaRef = doc(db, 'citas', id);
        await deleteDoc(citaRef);
        
        setSnackbar({
          open: true,
          message: "Cita eliminada correctamente",
          severity: 'info'
        });
        
        cargarCitas(); // Recargar las citas
      } catch (error) {
        console.error("Error al eliminar la cita:", error);
        setSnackbar({
          open: true,
          message: "Error al eliminar la cita: " + error.message,
          severity: 'error'
        });
      }
    }
  };

  const getCitasResumen = () => {
    const hoy = dayjs().format('YYYY-MM-DD');
    const citasHoy = citas.filter(cita => cita.fecha === hoy);
    const programadas = citasHoy.filter(cita => cita.estado === 'programada').length;
    const confirmadas = citasHoy.filter(cita => cita.estado === 'confirmada').length;
    const urgentes = citasHoy.filter(cita => cita.estado === 'urgente').length;
    
    return { total: citasHoy.length, programadas, confirmadas, urgentes };
  };
  
  // Función para manejar el cierre del snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Funciones de utilidad para manejo de citas

  const resumen = getCitasResumen();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <AdminLayout>
        <Container maxWidth="lg" sx={{ mt: isSmallMobile ? 2 : 4, mb: isSmallMobile ? 2 : 4, px: isSmallMobile ? 1 : 2 }}>
          <Typography 
            variant={isSmallMobile ? "h5" : "h4"} 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: isSmallMobile ? 2 : 3
            }}
          >
            <ScheduleIcon sx={{ mr: isSmallMobile ? 1 : 2, fontSize: isSmallMobile ? '1.5rem' : '2rem' }} />
            Agenda Privada
          </Typography>

          {/* Resumen del día */}
          <Grid container spacing={isSmallMobile ? 2 : 3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: isSmallMobile ? 1.5 : 2 }}>
                  <Typography variant={isSmallMobile ? "h5" : "h4"} color="primary">
                    {resumen.total}
                  </Typography>
                  <Typography variant={isSmallMobile ? "caption" : "body2"} color="text.secondary">
                    Citas Hoy
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: isSmallMobile ? 1.5 : 2 }}>
                  <Typography variant={isSmallMobile ? "h5" : "h4"} color="success.main">
                    {resumen.confirmadas}
                  </Typography>
                  <Typography variant={isSmallMobile ? "caption" : "body2"} color="text.secondary">
                    Confirmadas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: isSmallMobile ? 1.5 : 2 }}>
                  <Typography variant={isSmallMobile ? "h5" : "h4"} color="warning.main">
                    {resumen.programadas}
                </Typography>
                <Typography variant={isSmallMobile ? "caption" : "body2"} color="text.secondary">
                  Programadas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: isSmallMobile ? 1.5 : 2 }}>
                <Typography variant={isSmallMobile ? "h5" : "h4"} color="error.main">
                  {resumen.urgentes}
                </Typography>
                <Typography variant={isSmallMobile ? "caption" : "body2"} color="text.secondary">
                  Urgentes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Controles principales */}
        <Grid container spacing={isSmallMobile ? 2 : 3} sx={{ mt: 2 }}>
          {/* Panel de fecha */}
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                <Typography 
                  variant={isSmallMobile ? "subtitle1" : "h6"} 
                  sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
                >
                  <EventIcon sx={{ mr: 1, fontSize: isSmallMobile ? '1rem' : '1.25rem' }} /> 
                  Seleccionar Fecha
                </Typography>
                <DatePicker
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      fullWidth 
                      size={isSmallMobile ? "small" : "medium"}
                    />
                  )}
                  shouldDisableDate={(date) => {
                    // Opcionalmente puedes deshabilitar fechas pasadas
                    // return dayjs(date).format('YYYY-MM-DD') < dayjs().format('YYYY-MM-DD');
                    return false;
                  }}
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size={isSmallMobile ? "medium" : "large"}
                  startIcon={<AddIcon />}
                  onClick={handleNewAppointment}
                  sx={{ 
                    mt: 2, 
                    py: isSmallMobile ? 1.5 : 2,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: isSmallMobile ? '0.875rem' : '1rem'
                  }}
                >
                  {isSmallMobile ? "Nueva Cita" : "Agregar Nueva Cita"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Panel de búsqueda y filtros */}
          <Grid item xs={12} md={6} lg={8}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Typography 
                    variant={isSmallMobile ? "subtitle1" : "h6"} 
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <SearchIcon sx={{ mr: 1, fontSize: isSmallMobile ? '1rem' : '1.25rem' }} />
                    Buscar Citas
                  </Typography>
                  
                  {(searchTerm || filtroTipo || filtroEstado) && (
                    <Chip 
                      label={`${citasDelDia.length} ${citasDelDia.length === 1 ? 'resultado' : 'resultados'}`} 
                      color="primary" 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
                
                <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Buscar por nombre del paciente"
                    variant="outlined"
                    size={isSmallMobile ? "small" : "medium"}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={isSmallMobile ? "Ej: Rocky, Max..." : "Ej: Rocky, Max, Luna..."}
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <SearchIcon color="action" fontSize="small" />
                        </Box>
                      ),
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                  <FormControl size={isSmallMobile ? "small" : "medium"} sx={{ minWidth: { xs: '100%', md: 180 } }}>
                    <InputLabel>Tipo de Cita</InputLabel>
                    <Select
                      value={filtroTipo}
                      label="Tipo de Cita"
                      onChange={(e) => setFiltroTipo(e.target.value)}
                      fullWidth
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300
                          }
                        }
                      }}
                    >
                      <MenuItem value="">Todos los tipos</MenuItem>
                      {tiposCita.map((tipo) => (
                        <MenuItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size={isSmallMobile ? "small" : "medium"} sx={{ minWidth: { xs: '100%', md: 180 } }}>
                    <InputLabel>Estado de Cita</InputLabel>
                    <Select
                      value={filtroEstado}
                      label="Estado de Cita"
                      onChange={(e) => setFiltroEstado(e.target.value)}
                      fullWidth
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300
                          }
                        }
                      }}
                    >
                      <MenuItem value="">Todos los estados</MenuItem>
                      {estadosCita.map((estado) => (
                        <MenuItem key={estado.value} value={estado.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              component="span" 
                              sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%',
                                mr: 1,
                                backgroundColor: estado.value === 'programada' ? '#4CAF50' : 
                                               estado.value === 'confirmada' ? '#2196F3' : 
                                               estado.value === 'completada' ? '#9E9E9E' : 
                                               estado.value === 'urgente' ? '#F44336' : 
                                               estado.value === 'cancelada' ? '#FF9800' : '#9E9E9E'
                              }} 
                            />
                            {estado.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Vista de horarios */}
        <Grid container spacing={isSmallMobile ? 2 : 3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 3,
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Typography 
                    variant={isSmallMobile ? "h6" : "h5"} 
                    sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
                  >
                    <TimeIcon sx={{ mr: isSmallMobile ? 0.5 : 1, fontSize: isSmallMobile ? '1.25rem' : '1.5rem' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <span>Horarios</span>
                      {isSmallMobile && (
                        <Typography variant="caption" color="text.secondary">
                          {selectedDate.format('DD/MM/YYYY')}
                        </Typography>
                      )}
                      {!isSmallMobile && <span> - {selectedDate.format('DD/MM/YYYY')}</span>}
                    </Box>
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', md: 'flex-end' }
                  }}>
                    <Chip 
                      icon={<TimeIcon />}
                      label={`${citas.length} citas`} 
                      color="primary" 
                      variant="outlined"
                      size={isSmallMobile ? "small" : "medium"}
                    />
                    {selectedDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') && 
                     todosLosHorarios.length !== horariosDisponibles.length && (
                      <Chip
                        icon={<WarningIcon />}
                        label={isSmallMobile ? `${todosLosHorarios.length - horariosDisponibles.length} pasados` : `${todosLosHorarios.length - horariosDisponibles.length} horarios pasados`}
                        size={isSmallMobile ? "small" : "medium"}
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
                
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : horariosDisponibles.length === 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {selectedDate.format('YYYY-MM-DD') < dayjs().format('YYYY-MM-DD') 
                      ? "No se pueden agendar citas en fechas pasadas."
                      : "No hay horarios disponibles para hoy. Todas las horas del día han pasado."}
                  </Alert>
                ) : citas.length === 0 && horariosDisponibles.length > 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No hay citas programadas para este día. Hay {horariosDisponibles.length} horarios disponibles.
                  </Alert>
                ) : (
                  <Grid container spacing={isSmallMobile ? 1 : 2}>
                    {/* Primero mostrar las citas de horarios pasados (solo para hoy) */}
                    {selectedDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') && (
                      <>
                        {todosLosHorarios
                          .filter(hora => !horariosDisponibles.includes(hora))
                          .map(hora => {
                            const citaEnHora = citas.find(cita => 
                              cita.fecha === selectedDate.format('YYYY-MM-DD') && cita.hora === hora
                            );
                            
                            if (!citaEnHora) return null; // No mostrar horarios pasados sin cita
                            
                            return (
                              <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={hora}>
                                <Paper
                                  sx={{
                                    p: isSmallMobile ? 1.5 : 2,
                                    bgcolor: 'grey.300',
                                    color: 'text.secondary',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '8px',
                                    opacity: 0.7,
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden'
                                  }}
                                >
                                  <Box sx={{ 
                                    position: 'absolute', 
                                    top: 0, 
                                    right: 0, 
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    fontSize: isSmallMobile ? '0.6rem' : '0.7rem',
                                    fontWeight: 'bold',
                                    borderBottomLeftRadius: '8px'
                                  }}>
                                    Pasado
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight="bold">
                                      {hora}
                                    </Typography>
                                    <Chip
                                      label={citaEnHora.estado}
                                      size="small"
                                      sx={{ opacity: 0.7 }}
                                    />
                                  </Box>
                                  
                                  <Box sx={{ mt: 1 }}>
                                    <Typography 
                                      variant={isSmallMobile ? "body2" : "body1"} 
                                      fontWeight="bold" 
                                      sx={{ mb: 0.5 }}
                                    >
                                      {citaEnHora.paciente}
                                    </Typography>
                                    <Typography variant={isSmallMobile ? "caption" : "body2"}>
                                      Dueño: {citaEnHora.dueño}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                      Horario ya pasado, no se puede modificar
                                    </Typography>
                                  </Box>
                                </Paper>
                              </Grid>
                            );
                          })
                        }
                      </>
                    )}
                    
                    {/* Mostrar los horarios disponibles */}
                    {horariosDisponibles.map((hora) => {
                      // Buscar la cita en este horario entre todas las citas del día seleccionado
                      const citaEnHora = citas.find(cita => 
                        cita.fecha === selectedDate.format('YYYY-MM-DD') && cita.hora === hora
                      );
                      
                      // Determinar si la cita coincide con los filtros aplicados
                      const isOcupado = !!citaEnHora;
                      const coincideConFiltros = isOcupado && citasDelDia.some(c => c.id === citaEnHora.id);
                      
                      // Si hay filtros aplicados y la cita no coincide con ellos, ocultar este horario solo si hay búsqueda activa
                      const mostrarHorario = !isOcupado || coincideConFiltros || !(searchTerm || filtroTipo || filtroEstado);
                      
                      if (!mostrarHorario) {
                        return null; // No mostrar si hay filtros y no coincide
                      }
                      
                      // Definir el color según el estado de la cita
                      const getBgColor = () => {
                        if (!isOcupado) return 'background.paper';
                        
                        // Colores según el estado
                        switch(citaEnHora.estado) {
                          case 'programada': return '#4CAF50'; // Verde
                          case 'confirmada': return '#2196F3'; // Azul
                          case 'en_curso': return '#FF9800'; // Naranja
                          case 'completada': return '#9E9E9E'; // Gris
                          case 'cancelada': return '#F44336'; // Rojo
                          case 'no_asistio': return '#F44336'; // Rojo
                          case 'urgente': return '#D32F2F'; // Rojo oscuro
                          default: return 'primary.light';
                        }
                      };
                      
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={hora}>
                          <Paper
                            sx={{
                              p: isSmallMobile ? 1.5 : 2,
                              bgcolor: isOcupado ? getBgColor() : 'background.paper',
                              color: isOcupado ? 'white' : 'text.primary',
                              border: '1px solid',
                              borderColor: isOcupado ? 'transparent' : 'divider',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: isOcupado ? 3 : 1,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              '&:hover': {
                                transform: isSmallMobile ? 'none' : 'translateY(-2px)',
                                boxShadow: 4
                              }
                            }}
                            onClick={() => {
                              if (isOcupado) {
                                handleEditAppointment(citaEnHora);
                              } else {
                                setAppointmentData({
                                  ...appointmentData,
                                  fecha: selectedDate.format('YYYY-MM-DD'),
                                  hora: hora
                                });
                                setDialogOpen(true);
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant={isSmallMobile ? "h6" : "h5"} fontWeight="bold">
                                {hora}
                              </Typography>
                              {isOcupado ? (
                                <Chip
                                  label={citaEnHora.estado}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: isSmallMobile ? '0.65rem' : '0.75rem'
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="Disponible"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ fontSize: isSmallMobile ? '0.65rem' : '0.75rem' }}
                                />
                              )}
                            </Box>
                            
                            {isOcupado ? (
                              <Box sx={{ mt: 1, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography 
                                  variant={isSmallMobile ? "body2" : "body1"} 
                                  fontWeight="bold" 
                                  sx={{ mb: 0.5 }}
                                >
                                  {citaEnHora.paciente}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <Typography variant={isSmallMobile ? "caption" : "body2"}>
                                    Dueño: {isSmallMobile ? citaEnHora.dueño.substring(0, 15) + (citaEnHora.dueño.length > 15 ? '...' : '') : citaEnHora.dueño}
                                  </Typography>
                                </Box>
                                {citaEnHora.telefono && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <PhoneIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {citaEnHora.telefono}
                                    </Typography>
                                  </Box>
                                )}
                                {citaEnHora.email && (
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1, width: '100%' }}>
                                    <EmailIcon fontSize="small" sx={{ mt: 0.2, flexShrink: 0 }} />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2
                                      }}
                                    >
                                      {citaEnHora.email}
                                    </Typography>
                                  </Box>
                                )}
                                <Box sx={{ mt: 'auto', pt: 1 }}>
                                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    {citaEnHora.motivo}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                  <Chip
                                    label={citaEnHora.tipo}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                      color: 'white'
                                    }}
                                  />
                                  <Box>
                                    <IconButton 
                                      size="small" 
                                      sx={{ color: 'white' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditAppointment(citaEnHora);
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    {citaEnHora.email && (
                                      <IconButton 
                                        size="small" 
                                        sx={{ color: 'white' }}
                                        title="Reenviar recordatorio por email"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          const emailSent = await sendEmailReminder(citaEnHora);
                                          if (emailSent) {
                                            setSnackbar({
                                              open: true,
                                              message: "Recordatorio enviado por email",
                                              severity: 'success'
                                            });
                                          } else {
                                            setSnackbar({
                                              open: true,
                                              message: "Error al enviar recordatorio por email",
                                              severity: 'error'
                                            });
                                          }
                                        }}
                                      >
                                        <EmailIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    <IconButton 
                                      size="small" 
                                      sx={{ color: 'white' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteAppointment(citaEnHora.id);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ 
                                mt: 2, 
                                flexGrow: 1, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                minHeight: '120px',
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2
                              }}>
                                <AddIcon sx={{ 
                                  fontSize: isSmallMobile ? 24 : 36, 
                                  opacity: 0.4, 
                                  mb: 1, 
                                  color: 'success.main' 
                                }} />
                                <Typography 
                                  variant={isSmallMobile ? "body2" : "body1"} 
                                  color="success.main" 
                                  textAlign="center" 
                                  fontWeight="medium"
                                >
                                  Disponible
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  textAlign="center" 
                                  sx={{ mt: 1 }}
                                >
                                  {isSmallMobile ? "Toca para agendar" : "Haz clic para programar una cita"}
                                </Typography>
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Dialog para nueva/editar cita */}
        <Dialog 
          open={dialogOpen} 
          onClose={() => setDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{
            sx: {
              ...(isSmallMobile && {
                m: 0,
                borderRadius: 0,
                maxHeight: '100vh'
              }),
              ...(!isSmallMobile && {
                borderRadius: 2
              })
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontSize: isSmallMobile ? '1.1rem' : '1.25rem',
            py: isSmallMobile ? 1.5 : 2
          }}>
            {editingAppointment ? 'Editar Cita' : 'Nueva Cita'}
          </DialogTitle>
          <DialogContent sx={{ 
            pb: isSmallMobile ? 10 : 2,
            px: isSmallMobile ? 2 : 3,
            pt: isSmallMobile ? 2 : 3,
            maxHeight: isSmallMobile ? 'calc(100vh - 120px)' : 'none',
            overflowY: 'auto'
          }}>
            <Grid container spacing={isSmallMobile ? 2 : 3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fecha"
                  type="date"
                  name="fecha"
                  value={appointmentData.fecha}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Hora</InputLabel>
                  <Select
                    name="hora"
                    value={appointmentData.hora}
                    onChange={handleInputChange}
                  >
                    {todosLosHorarios.map((hora) => {
                      // Validar si la hora está disponible
                      const ahora = dayjs();
                      const horaCita = dayjs(`${appointmentData.fecha} ${hora}`, 'YYYY-MM-DD HH:mm');
                      
                      // Si está editando una cita existente, permitir todas las horas
                      if (editingAppointment) {
                        return (
                          <MenuItem key={hora} value={hora}>
                            {hora}
                          </MenuItem>
                        );
                      }
                      
                      // Para citas nuevas, verificar si la hora ya pasó
                      const isHoraDisponible = horaCita.isAfter(ahora);
                      
                      return (
                        <MenuItem 
                          key={hora} 
                          value={hora}
                          disabled={!isHoraDisponible}
                          style={{ 
                            color: !isHoraDisponible ? '#999' : 'inherit'
                          }}
                        >
                          {hora} {!isHoraDisponible && " (Ya pasó)"}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Paciente</InputLabel>
                  <Select
                    value={tipoPacienteCita}
                    label="Tipo de Paciente"
                    onChange={handleTipoPacienteCitaChange}
                  >
                    <MenuItem value="nuevo">Paciente nuevo</MenuItem>
                    <MenuItem value="existente">Paciente existente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {tipoPacienteCita === 'existente' ? (
                <>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={pacientesExistentes}
                      loading={loadingPacientesExistentes}
                      value={pacientesExistentes.find((p) => p.id === pacienteSeleccionadoId) || null}
                      onChange={handleAutocompletePacienteExistente}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => {
                        const nombre = option?.nombre || 'Sin nombre';
                        const propietario = option?.propietario || option?.['dueño'] || option?.dueno || 'Sin dueño';
                        return `${nombre} - ${propietario}`;
                      }}
                      filterOptions={(options, state) => {
                        const term = state.inputValue.trim().toLowerCase();
                        if (!term) return options;
                        return options.filter((option) => {
                          const nombre = String(option?.nombre || '').toLowerCase();
                          const propietario = String(option?.propietario || option?.['dueño'] || option?.dueno || '').toLowerCase();
                          return nombre.includes(term) || propietario.includes(term);
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Paciente existente"
                          placeholder="Escribe nombre del paciente o propietario"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Datos del propietario cargados automáticamente desde el paciente seleccionado.
                    </Alert>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Paciente"
                      name="paciente"
                      value={appointmentData.paciente}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Dueño"
                      name="dueño"
                      value={appointmentData.dueño}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>
                </>
              )}

              {tipoPacienteCita === 'existente' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Paciente"
                      value={appointmentData.paciente}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Dueño"
                      value={appointmentData.dueño}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="telefono"
                  value={
                    tipoPacienteCita === 'existente' && !appointmentData.telefono
                      ? 'Sin teléfono'
                      : appointmentData.telefono
                  }
                  onChange={handleInputChange}
                  InputProps={{ readOnly: tipoPacienteCita === 'existente' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  name="email"
                  type="email"
                  value={
                    tipoPacienteCita === 'existente' && !appointmentData.email
                      ? 'Sin correo'
                      : appointmentData.email
                  }
                  onChange={handleInputChange}
                  placeholder="ejemplo@email.com"
                  helperText="Para enviar recordatorios de la cita"
                  InputProps={{ readOnly: tipoPacienteCita === 'existente' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Cita</InputLabel>
                  <Select
                    name="tipo"
                    value={appointmentData.tipo}
                    onChange={handleInputChange}
                  >
                    {tiposCita.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Motivo de la cita"
                  value={appointmentData.motivo}
                  onChange={(e) => setAppointmentData({...appointmentData, motivo: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    name="estado"
                    value={appointmentData.estado}
                    onChange={handleInputChange}
                  >
                    {estadosCita.map((estado) => (
                      <MenuItem key={estado.value} value={estado.value}>
                        {estado.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observaciones"
                  name="observaciones"
                  value={appointmentData.observaciones}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                />
              </Grid>
              
              {/* Sección de Citas Recurrentes - Solo visible para nuevas citas */}
              {!editingAppointment && (
                <>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appointmentData.esRecurrente}
                          onChange={(e) => setAppointmentData({
                            ...appointmentData,
                            esRecurrente: e.target.checked
                          })}
                        />
                      }
                      label="Cita Recurrente"
                    />
                  </Grid>
                  
                  {appointmentData.esRecurrente && (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Frecuencia</InputLabel>
                          <Select
                            name="tipoRecurrencia"
                            value={appointmentData.tipoRecurrencia}
                            onChange={handleInputChange}
                          >
                            {tiposRecurrencia.map((tipo) => (
                              <MenuItem key={tipo.value} value={tipo.value}>
                                {tipo.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Número de Citas"
                          name="cantidadRecurrencias"
                          type="number"
                          value={appointmentData.cantidadRecurrencias}
                          onChange={handleInputChange}
                          InputProps={{ inputProps: { min: 1, max: 12 } }}
                        />
                      </Grid>
                    </>
                  )}
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            position: isSmallMobile ? 'fixed' : 'static',
            bottom: isSmallMobile ? 0 : 'auto',
            left: isSmallMobile ? 0 : 'auto',
            right: isSmallMobile ? 0 : 'auto',
            bgcolor: isSmallMobile ? 'background.paper' : 'transparent',
            borderTop: isSmallMobile ? '1px solid' : 0,
            borderColor: 'divider',
            p: isSmallMobile ? 2.5 : 1.5,
            zIndex: 2,
            gap: 1,
            justifyContent: isSmallMobile ? 'space-between' : 'flex-end',
            boxShadow: isSmallMobile ? '0 -2px 8px rgba(0,0,0,0.1)' : 'none'
          }}>
            <Button 
              onClick={() => setDialogOpen(false)}
              variant={isSmallMobile ? "outlined" : "text"}
              size={isSmallMobile ? "large" : "medium"}
              sx={{ 
                width: isSmallMobile ? '45%' : 'auto',
                textTransform: 'none'
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveAppointment} 
              variant="contained"
              size={isSmallMobile ? "large" : "medium"}
              sx={{ 
                width: isSmallMobile ? '45%' : 'auto',
                textTransform: 'none'
              }}
            >
              {editingAppointment ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ 
            vertical: 'top', 
            horizontal: 'center' 
          }}
          sx={{
            mt: 10, // Margen superior para que aparezca debajo de la barra de navegación
            '& .MuiSnackbarContent-root': {
              maxWidth: isSmallMobile ? '90vw' : '500px',
              fontSize: isSmallMobile ? '0.875rem' : '1rem'
            }
          }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ 
              width: '100%',
              fontSize: isSmallMobile ? '0.875rem' : '1rem'
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        
        {/* Indicador de carga */}
        {loading && (
          <Box sx={{ 
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000
          }}>
            <CircularProgress size={isSmallMobile ? 50 : 60} />
          </Box>
        )}
        </Container>
      </AdminLayout>
    </LocalizationProvider>
  );
};

export default AgendaPrivada;
